import {
  XHV_META_SPEC,
  type JsonRecord,
  type JsonSchema,
  type JsonSchemaObject,
  type JsonSchemaPrimitive,
  type JsonType,
  type SchemaTypeValue,
  type XHvMeta,
} from '@/domain/entities/property-schema';
import type { AttributeNode } from '@/types/schemas';
import type { FlatField, SchemaSwitchLog } from './types';
import type { HomeSystemType } from '@/domain/entities/system';
import { normalizeXhvValue } from '../property-schemas/utils/schema-utils';
import type { TemplateAudience } from '@/domain/entities/preperty-template';

export function uid(prefix = 'id') {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isJsonRecord(v: unknown): v is JsonRecord {
  return typeof v === 'object' && v != null && !Array.isArray(v);
}

const TEMPLATE_AUDIENCES = ['business', 'consumer', 'both'] as const satisfies readonly TemplateAudience[];

export type TemplateImportPayload = {
  title: string;
  description: string;
  audience?: TemplateAudience;
  data: JsonRecord;
};

export type TemplateImportValidationResult =
  | { ok: true; template: TemplateImportPayload }
  | { ok: false; errors: string[] };

export function validateTemplateImportPayload(payload: unknown): TemplateImportValidationResult {
  const errors: string[] = [];

  if (!isJsonRecord(payload)) {
    return { ok: false, errors: ['Payload must be a JSON object.'] };
  }

  const title = payload.title;
  const description = payload.description;
  const audience = payload.audience;
  const data = payload.data;

  if (title != null && typeof title !== 'string') {
    errors.push('title must be a string when provided.');
  }

  if (description != null && typeof description !== 'string') {
    errors.push('description must be a string when provided.');
  }

  if (
    audience != null &&
    (!TEMPLATE_AUDIENCES.includes(audience as TemplateAudience) || typeof audience !== 'string')
  ) {
    errors.push('audience must be one of: business, consumer, both.');
  }

  if (data != null && !isJsonRecord(data)) {
    errors.push('data must be a JSON object when provided.');
  }

  if (errors.length) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    template: {
      title: typeof title === 'string' ? title : '',
      description: typeof description === 'string' ? description : '',
      audience: typeof audience === 'string' ? (audience as TemplateAudience) : undefined,
      data: isJsonRecord(data) ? data : {},
    },
  };
}

function isFieldType(v: unknown): v is XHvMeta['fieldType'] {
  const opts = XHV_META_SPEC.fieldType.options;
  return typeof v === 'string' && (opts as readonly string[]).includes(v);
}

export function readXHv(schema: unknown): XHvMeta | undefined {
  if (typeof schema !== 'object' || schema == null) return undefined;

  const hvRaw = (schema as Record<string, unknown>)['x-hv'];
  if (typeof hvRaw !== 'object' || hvRaw == null || Array.isArray(hvRaw)) return undefined;

  const hvObj = hvRaw as Record<string, unknown>;

  const out: Partial<XHvMeta> = {};

  // ✅ systemTypes (array OR string → normalize)
  if (isStringArray(hvObj.systemTypes)) {
    out.systemTypes = hvObj.systemTypes;
  } else if (typeof hvObj.systemTypes === 'string') {
    const v = normalizeXhvValue('systemTypes', hvObj.systemTypes);
    if (isStringArray(v)) out.systemTypes = v;
  }

  // ✅ excludeSystemTypes (array OR string → normalize)
  if (isStringArray(hvObj.excludeSystemTypes)) {
    out.excludeSystemTypes = hvObj.excludeSystemTypes;
  } else if (typeof hvObj.excludeSystemTypes === 'string') {
    const v = normalizeXhvValue('excludeSystemTypes', hvObj.excludeSystemTypes);
    if (isStringArray(v)) out.excludeSystemTypes = v;
  }

  // fieldType (unchanged)
  if (isFieldType(hvObj.fieldType)) {
    out.fieldType = hvObj.fieldType;
  }

  // nothing valid => ignore
  return Object.keys(out).length ? (out as XHvMeta) : undefined;
}
/** ------------------------------------------------------------------ */
/** Utilities (copied/adapted from your schema builder)                  */
/** ------------------------------------------------------------------ */

export function nowUnixSeconds() {
  return Math.floor(Date.now() / 1000);
}

function isNullableType(type: SchemaTypeValue): { base: JsonType; nullable: boolean } {
  if (Array.isArray(type)) {
    const nullable = type.includes('null');
    const base = (type.find((t) => t !== 'null') ?? 'string') as JsonType;
    return { base, nullable };
  }
  return { base: type as JsonType, nullable: false };
}

function schemaToNode(key: string, schema: JsonSchema, requiredKeys: Set<string>): AttributeNode {
  const { base, nullable } = isNullableType(schema.type);

  if (base === 'object') {
    const s = schema as JsonSchemaObject;
    const childRequired = new Set<string>(s.required ?? []);
    const children = Object.entries(s.attributes ?? {}).map(([k, v]) => schemaToNode(k, v, childRequired));
    return {
      id: uid('attr'),
      key,
      type: 'object',
      nullable,
      required: requiredKeys.has(key),
      attributes: children,
      xHv: readXHv(schema),
    };
  }

  if (base === 'array') {
    const s = schema as { type: SchemaTypeValue; items: JsonSchema };
    const itemNode = schemaToNode('item', s.items, new Set());
    return {
      id: uid('attr'),
      key,
      type: 'array',
      nullable,
      required: requiredKeys.has(key),
      items: { ...itemNode, key: 'item' },
      xHv: readXHv(schema),
    };
  }

  const s = schema as JsonSchemaPrimitive;
  return {
    id: uid('attr'),
    key,
    type: base,
    nullable,
    required: requiredKeys.has(key),
    enum: base === 'string' ? s.enum : undefined,
    xHv: readXHv(schema),
  };
}

export function schemaToBlocks(root: JsonSchemaObject): AttributeNode[] {
  const rootRequired = new Set<string>(root.required ?? []);
  return Object.entries(root.attributes ?? {}).map(([k, v]) => schemaToNode(k, v, rootRequired));
}

export function flattenTree(nodes: AttributeNode[], parentPath: string | null = null): FlatField[] {
  const out: FlatField[] = [];
  for (const n of nodes) {
    const path = parentPath ? `${parentPath}.${n.key}` : n.key;
    out.push({ path, node: n, parentPath });

    if (n.type === 'object' && n.attributes?.length) {
      out.push(...flattenTree(n.attributes, path));
    }

    if (n.type === 'array' && n.items) {
      const itemPath = `${path}.${n.items.key ?? 'item'}`; // you set key:'item' already
      out.push({ path: itemPath, node: n.items, parentPath: path });

      if (n.items.type === 'object' && n.items.attributes?.length) {
        out.push(...flattenTree(n.items.attributes, itemPath));
      }
    }
  }
  return out;
}

const isIndex = (s: string) => /^\d+$/.test(s);

export function getAtPath(obj: unknown, path: string): unknown | undefined {
  if (!path) return obj;
  const parts = path.split('.');
  let cur: unknown = obj;

  for (const p of parts) {
    if (cur == null) return undefined;

    if (Array.isArray(cur)) {
      if (!isIndex(p)) return undefined;
      cur = cur[Number(p)];
      continue;
    }

    if (typeof cur === 'object') {
      cur = (cur as JsonRecord)[p];
      continue;
    }

    return undefined;
  }

  return cur;
}
export function setAtPath(obj: JsonRecord, path: string, value: unknown): JsonRecord {
  const parts = path.split('.');
  const root: JsonRecord = { ...(obj ?? {}) };
  let cur: unknown = root;

  const isIndex = (s: string) => /^\d+$/.test(s);

  for (let i = 0; i < parts.length; i++) {
    const k = parts[i];
    const isLast = i === parts.length - 1;
    const nextKey = parts[i + 1];

    if (Array.isArray(cur)) {
      const idx = Number(k);
      if (!Number.isFinite(idx)) return root;

      if (isLast) {
        cur[idx] = value;
      } else {
        const existing = cur[idx];
        const nextContainer = isIndex(nextKey)
          ? Array.isArray(existing)
            ? [...existing]
            : []
          : typeof existing === 'object' && existing
            ? { ...(existing as JsonRecord) }
            : {};
        cur[idx] = nextContainer;
        cur = nextContainer;
      }
      continue;
    }

    const o = cur as JsonRecord;

    if (isLast) {
      o[k] = value;
    } else {
      const existing = o[k];
      const nextContainer = isIndex(nextKey)
        ? Array.isArray(existing)
          ? [...existing]
          : []
        : typeof existing === 'object' && existing
          ? { ...(existing as JsonRecord) }
          : {};
      o[k] = nextContainer;
      cur = nextContainer;
    }
  }

  return root;
}

export function unsetAtPath(obj: JsonRecord, path: string): JsonRecord {
  const parts = path.split('.');
  const root: JsonRecord = { ...(obj ?? {}) };

  const isIndex = (s: string) => /^\d+$/.test(s);

  let cur: unknown = root;
  const stack: Array<{ parent: unknown; key: string }> = [];

  for (const p of parts) {
    if (cur == null) return root;
    stack.push({ parent: cur, key: p });

    if (Array.isArray(cur)) {
      if (!isIndex(p)) return root;
      cur = cur[Number(p)];
    } else if (typeof cur === 'object') {
      cur = (cur as JsonRecord)[p];
    } else {
      return root;
    }
  }

  const last = stack.pop();
  if (!last) return root;

  const { parent, key } = last;

  if (Array.isArray(parent)) {
    if (isIndex(key)) parent.splice(Number(key), 1);
  } else if (typeof parent === 'object' && parent) {
    delete (parent as JsonRecord)[key];
  }

  return root;
}

export function coerceValue(node: AttributeNode, raw: string | boolean): unknown {
  if (node.type === 'boolean') return Boolean(raw);
  if (node.type === 'number') {
    const n = typeof raw === 'string' ? Number(raw) : Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  // string + enums
  if (node.type === 'string') return String(raw);
  // object/array handled elsewhere
  return raw;
}

// Mark included = true for any path that exists in data (including nested)
// For arrays: mark the array path and also mark children paths with numeric indices (same format your setAtPath uses)
export function buildIncludedFromData(
  data: unknown,
  parentPath: string | null = null,
  out: Record<string, boolean> = {},
) {
  if (data == null) return out;

  if (Array.isArray(data)) {
    if (parentPath) out[parentPath] = true;
    data.forEach((item, idx) => {
      const nextPath = parentPath ? `${parentPath}.${idx}` : String(idx);
      buildIncludedFromData(item, nextPath, out);
    });
    return out;
  }

  if (typeof data === 'object') {
    if (parentPath) out[parentPath] = true;
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      const nextPath = parentPath ? `${parentPath}.${k}` : k;
      out[nextPath] = true;
      buildIncludedFromData(v, nextPath, out);
    }
  }

  return out;
}

/** Include rules:
 * - If a leaf is unchecked: remove that path from data
 * - If a parent object is unchecked: remove the entire subtree (all descendants) + the parent path
 */
export function collectDescendants(flat: FlatField[], path: string): string[] {
  const prefix = `${path}.`;
  return flat.filter((f) => f.path === path || f.path.startsWith(prefix)).map((f) => f.path);
}

/**
 * Utility: check if the given metadata allows inclusion for the given system type
 * @param meta
 * @param systemType
 * @returns
 */
export function isAllowedForSystemType(meta: XHvMeta | undefined, systemType: string | undefined): boolean {
  //   console.log('$$ isAllowedForSystemType:', { meta, systemType });
  if (!meta) return true; // no metadata => always show
  if (!systemType) return true; // nothing selected yet => show all (or choose false if you prefer)

  const include = meta.systemTypes;
  const exclude = meta.excludeSystemTypes;

  if (Array.isArray(exclude) && exclude.includes(systemType)) {
    // console.log('$$ excluded:', systemType);
    return false;
  }
  if (Array.isArray(include) && include.length > 0) {
    const ss = include.includes(systemType);
    // console.log('$$ included:', systemType, ss);
    return ss;
  }

  return true;
}

/**
 * Utility: filter the configuration object schema for systems that match the given system type
 * @param configurationSchema
 * @param systemType
 * @returns
 */
export function filterSystemConfigurationSchema(
  configurationSchema: JsonSchemaObject,
  systemType: HomeSystemType | undefined,
): JsonSchemaObject {
  const nextAttributes: Record<string, JsonSchema> = {};

  for (const [key, childSchema] of Object.entries(configurationSchema.attributes ?? {})) {
    const meta = (childSchema as { ['x-hv']?: unknown })['x-hv'];
    // safe narrow
    const xhv = (typeof meta === 'object' && meta != null ? (meta as Record<string, unknown>) : null) ?? null;

    const typedMeta = xhv
      ? {
          systemTypes: Array.isArray(xhv.systemTypes)
            ? xhv.systemTypes.filter((v): v is string => typeof v === 'string')
            : undefined,
          excludeSystemTypes: Array.isArray(xhv.excludeSystemTypes)
            ? xhv.excludeSystemTypes.filter((v): v is string => typeof v === 'string')
            : undefined,
        }
      : undefined;

    if (isAllowedForSystemType(typedMeta, systemType)) {
      nextAttributes[key] = childSchema;
    }
  }

  return { ...configurationSchema, attributes: nextAttributes };
}

export function sanitizeTemplateDataToSchema(
  blocks: AttributeNode[],
  data: JsonRecord,
): { data: JsonRecord; log: SchemaSwitchLog } {
  const flat = flattenTree(blocks);
  const allowed = new Set(flat.map((f) => f.path));
  const findSchemaNode = (path: string) => flat.find((f) => f.path === path)?.node;

  const removedPaths: string[] = [];
  const mismatchedPaths: string[] = [];

  const walk = (val: unknown, dataPath: string, schemaPath = dataPath): unknown => {
    const schemaNode = findSchemaNode(schemaPath);

    if (Array.isArray(val)) {
      if (schemaNode && schemaNode.type !== 'array') {
        mismatchedPaths.push(dataPath);
        return val;
      }

      // If schema says this is array of objects, sanitize each object item
      if (schemaNode?.type === 'array' && schemaNode.items?.type === 'object') {
        const allowedKeys = new Set((schemaNode.items.attributes ?? []).map((a) => a.key));

        return val.map((item, idx) => {
          if (typeof item !== 'object' || item == null || Array.isArray(item)) return item;

          const obj = item as Record<string, unknown>;
          const out: Record<string, unknown> = {};

          for (const [k, v] of Object.entries(obj)) {
            const itemDataPath = `${dataPath}.${idx}.${k}`;
            const itemSchemaPath = `${schemaPath}.item.${k}`;

            if (!allowedKeys.has(k)) {
              removedPaths.push(itemDataPath);
              continue;
            }

            const childSchemaNode = findSchemaNode(itemSchemaPath);
            if (childSchemaNode) {
              const isObj = typeof v === 'object' && v != null && !Array.isArray(v);
              const isArr = Array.isArray(v);

              if (childSchemaNode.type === 'object' && !isObj) {
                mismatchedPaths.push(itemDataPath);
                out[k] = v;
                continue;
              }
              if (childSchemaNode.type === 'array' && !isArr) {
                mismatchedPaths.push(itemDataPath);
                out[k] = v;
                continue;
              }
            }

            out[k] = walk(v, itemDataPath, itemSchemaPath);
          }

          return out;
        });
      }

      return val;
    }

    if (typeof val !== 'object' || val == null) {
      return val;
    }

    if (schemaNode && schemaNode.type !== 'object') {
      mismatchedPaths.push(dataPath);
      return val;
    }

    const obj = val as Record<string, unknown>;
    const out: Record<string, unknown> = {};

    for (const [k, v] of Object.entries(obj)) {
      const nextDataPath = dataPath ? `${dataPath}.${k}` : k;
      const nextSchemaPath = schemaPath ? `${schemaPath}.${k}` : k;

      // If the exact path isn't in schema => remove it
      if (!allowed.has(nextSchemaPath)) {
        removedPaths.push(nextDataPath);
        continue;
      }

      // Optional: very light mismatch detection (object/array only)
      // If schema expects object but value isn't, etc.
      const childSchemaNode = findSchemaNode(nextSchemaPath);
      if (childSchemaNode) {
        const expected = childSchemaNode.type;

        const isObj = typeof v === 'object' && v != null && !Array.isArray(v);
        const isArr = Array.isArray(v);

        if (expected === 'object' && !isObj) {
          mismatchedPaths.push(nextDataPath);
          out[k] = v;
          continue;
        }
        if (expected === 'array' && !isArr) {
          mismatchedPaths.push(nextDataPath);
          out[k] = v;
          continue;
        }
      }

      out[k] = walk(v, nextDataPath, nextSchemaPath);
    }

    return out;
  };

  return {
    data: walk(data, '') as JsonRecord,
    log: { removedPaths, mismatchedPaths },
  };
}
